<template>
  <div class="restaurant-station-select" :class="{ 'form-group': !compact, 'restaurant-station-select--compact': compact }">
    <label v-if="!compact" :for="controlId" class="form-label">{{ $t('station.assignment') }}</label>
    <div class="restaurant-station-select__control" :class="{ 'is-assigned': selectedStation !== '' && !conflict, 'is-conflict': conflict }" :aria-busy="loading || busy ? 'true' : 'false'">
      <button v-if="tooltipText" ref="help" type="button" class="restaurant-station-select__hint"
        v-tooltip="{ content: tooltipText, html: false, container: 'body', triggers: ['hover', 'focus', 'click'],
          hideTriggers: ['hover', 'focus'], popperTriggers: ['hover'], autoHide: hideHelpOnOutside, delay: 0,
          noAutoFocus: true, autoBoundaryMaxSize: true, overflowPadding: 8 }"
        :class="{ 'text-danger': error }" :aria-label="tooltipText" @click.stop
        @keydown.esc.stop.prevent="hideHelp">
        <CIcon name="cil-info" aria-hidden="true" />
      </button>
      <span v-else class="restaurant-station-select__prefix" aria-hidden="true">
        <CIcon name="cil-location-pin" class="restaurant-station-select__icon" aria-hidden="true" />
      </span>
      <select :id="controlId" ref="control" v-model="selectedStation" class="form-control" :class="{ 'form-control-sm': compact, 'is-invalid': error }"
        :aria-label="productName ? $t('station.assignment_for', { product: productName }) : $t('station.assignment')"
        :aria-describedby="helpText || busy ? controlId + '-help' : null" :aria-invalid="error ? 'true' : null"
        :disabled="isDisabled">
        <option value="">{{ emptyOptionLabel }}</option>
        <option v-for="station in compatibleStations" :key="station.id" :value="station.id">{{ station.name }}</option>
      </select>
      <span v-if="selectedStation !== '' && !loading && !busy && !error" class="restaurant-station-select__state" aria-hidden="true"></span>
      <CSpinner v-if="loading || busy" size="sm" class="restaurant-station-select__spinner" aria-hidden="true" />
    </div>
    <small v-if="helpText || busy" :id="controlId + '-help'" class="restaurant-station-select__help sr-only"
      :role="error ? 'alert' : 'status'">
      {{ busy ? $t('station.saving') : helpText }}
    </small>
    <router-link v-if="showManageLink" :to="{ name: 'RestaurantStations' }" class="restaurant-station-select__link">
      {{ $t('station.manage') }}
    </router-link>
  </div>
</template>

<script>
import api from '@/api';
import { loadStations, stationAcceptsProduct } from '@/services/restaurantStations';
import { lifecycleErrorMessage } from '@/helper/lifecycleFeedback';

export default {
  name: 'RestaurantStationSelect',
  props: {
    value: { type: [Number, String], default: null },
    firmId: { type: [Number, String], required: true },
    cookStation: { type: Boolean, default: null },
    conflict: { type: Object, default: null },
    compact: Boolean,
    instant: Boolean,
    disabled: Boolean,
    manageLink: { type: Boolean, default: true },
    productName: { type: String, default: '' },
    productId: { type: [Number, String], default: null },
  },
  data() {
    return {
      state: { ready: false, stations: [] }, loading: true, busy: false, error: '',
      controlId: 'station-select-' + this._uid, requestGeneration: 0, assignmentCleared: false, kitchenChangedWhileLoading: false,
    };
  },
  computed: {
    selectedStation: {
      get() { return this.value == null ? '' : this.value; },
      set(value) { this.change(value); },
    },
    normalizedCookStation() { return this.cookStation ?? false; },
    isKitchenPrepared() { return this.normalizedCookStation === true; },
    compatibleStations() { return this.state.stations.filter(station => stationAcceptsProduct(station, this.normalizedCookStation)); },
    isDisabled() { return this.disabled || this.loading || this.busy || !this.state.ready; },
    emptyOptionLabel() {
      if (this.loading) return this.$t('station.loading');
      if (this.conflict) return this.$t('station.review_required');
      return this.$t('station.none');
    },
    helpText() {
      if (this.loading) return '';
      if (this.error) return this.error;
      if (this.assignmentCleared) return this.$t('station.incompatible_assignment_removed');
      if (this.conflict) return this.$t('station.conflict');
      if (!this.state.ready) return this.$t('station.import_first');
      if (!this.state.stations.length) return this.$t('station.no_stations_hint');
      if (!this.compatibleStations.length) return this.$t(this.isKitchenPrepared ? 'station.no_food_stations' : 'station.no_drinks_stations');
      return this.$t(this.isKitchenPrepared ? 'station.food_assignment_help' : 'station.drinks_assignment_help');
    },
    tooltipText() { return this.busy ? '' : this.helpText; },
    showManageLink() {
      return this.manageLink && !this.loading
        && (this.conflict || !this.state.ready || !this.compatibleStations.length);
    },
  },
  watch: {
    firmId: { immediate: true, handler: 'load' },
    cookStation(value, previous) {
      if ((value ?? false) === (previous ?? false)) return;
      this.kitchenChangedWhileLoading = this.loading;
      this.clearIncompatibleSelection();
    },
    value(value, previous) {
      const oldStation = this.state.stations.find(station => String(station.id) === String(previous));
      if (value == null && oldStation && !stationAcceptsProduct(oldStation, this.normalizedCookStation)) this.assignmentCleared = true;
    },
  },
  beforeDestroy() { this.requestGeneration++; },
  methods: {
    hideHelpOnOutside(event) { return !this.$refs.help?.contains(event.target); },
    hideHelp() { this.$refs.help?.$_popper?.hide(); },
    clearIncompatibleSelection() {
      const current = this.state.stations.find(station => String(station.id) === String(this.value));
      if (current && !stationAcceptsProduct(current, this.normalizedCookStation)) {
        this.assignmentCleared = true;
        this.$emit('input', null);
      }
    },
    async load() {
      const generation = ++this.requestGeneration;
      const firmId = this.firmId;
      this.loading = true;
      this.busy = false;
      this.state = { ready: false, stations: [] };
      this.error = '';
      this.assignmentCleared = false;
      this.kitchenChangedWhileLoading = false;
      if (!firmId) { this.loading = false; return; }
      try {
        const state = await loadStations(this.$axios, firmId);
        if (this.firmId === firmId && generation === this.requestGeneration) {
          this.state = state;
          if (this.kitchenChangedWhileLoading) { this.clearIncompatibleSelection(); this.kitchenChangedWhileLoading = false; }
        }
      } catch (error) {
        if (this.firmId === firmId && generation === this.requestGeneration) {
          this.error = error.response?.data?.message || this.$t('station.load_failed');
        }
      } finally {
        if (generation === this.requestGeneration) this.loading = false;
      }
    },
    async change(value) {
      if (this.isDisabled) return;
      const stationId = value === '' ? null : Number(value);
      if (stationId !== null && !this.compatibleStations.some(station => station.id === stationId)) return;
      this.error = '';
      this.assignmentCleared = false;
      if (!this.instant) {
        this.$emit('input', stationId);
        return;
      }
      const firmId = this.firmId;
      const generation = this.requestGeneration;
      this.busy = true;
      try {
        const response = await this.$axios.put(api.restaurants.stations.routing(firmId, this.productId),
          { station_id: stationId, cook_station: this.isKitchenPrepared });
        if (generation !== this.requestGeneration) return;
        this.$emit('input', response.data.station_id);
        this.$emit('updated', response.data);
      } catch (error) {
        if (generation !== this.requestGeneration) return;
        this.error = error.response?.data?.code ? lifecycleErrorMessage(this, error) : this.$t('station.save_failed');
        this.$forceUpdate();
      } finally {
        if (generation === this.requestGeneration) this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.restaurant-station-select { min-width: 0; }
.restaurant-station-select--compact { min-width: 12rem; max-width: 24rem; }
.restaurant-station-select--compact .restaurant-station-select__control { min-height: 2.25rem; border-radius: 0.5rem; }
.restaurant-station-select--compact .restaurant-station-select__control select { min-height: 2.25rem; height: 2.25rem; padding-top: 0.25rem; padding-bottom: 0.25rem; padding-left: 2rem; padding-right: 1.75rem; font-size: 0.85rem; border-radius: 0.5rem; text-overflow: ellipsis; }
.restaurant-station-select--compact .restaurant-station-select__prefix { left: 0.6rem; }
.restaurant-station-select--compact .restaurant-station-select__hint { width: 2rem; height: 2.25rem; }
.restaurant-station-select__control {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 2.75rem;
  border-radius: 0.875rem;
  border: 1px solid #dfe3ec;
  background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 0 0 rgba(76, 96, 218, 0.08);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, transform 0.18s ease;
}
.restaurant-station-select__control:focus-within {
  border-color: rgba(76, 96, 218, 0.6);
  box-shadow: 0 0 0 4px rgba(76, 96, 218, 0.12);
}
.restaurant-station-select__control select {
  width: 100%;
  min-height: 2.75rem;
  padding-left: 2.4rem;
  padding-right: 2.4rem;
  border: 0;
  border-radius: 0.875rem;
  background: transparent;
  color: #1f2937;
  font-weight: 500;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  -webkit-appearance: auto;
  -moz-appearance: auto;
  appearance: auto;
}
.restaurant-station-select__control select:disabled {
  color: #475569;
  cursor: not-allowed;
}
.restaurant-station-select__control select::-ms-expand { display: none; }
.restaurant-station-select__control select:focus {
  outline: none;
  box-shadow: none;
}
.restaurant-station-select__control select option { color: #1f2937; padding: 0.2rem 0.4rem; margin: 0; font-size: 0.85rem; }
.restaurant-station-select__prefix {
  position: absolute;
  top: 50%;
  left: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: auto;
}
.restaurant-station-select__icon {
  width: 1rem;
  height: 1rem;
  color: #8a93a2;
}
.restaurant-station-select__hint {
  position: absolute;
  inset: 0 auto 0 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  padding: 0;
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: var(--info, #39f);
  cursor: help;
  touch-action: manipulation;
}
.restaurant-station-select__hint:focus-visible { outline: 2px solid var(--aw-form-focus-border, #4c60da); outline-offset: -3px; }
.restaurant-station-select__hint ::v-deep .c-icon { width: 1rem; height: 1rem; pointer-events: none; }
.restaurant-station-select__disabled-icon {
  width: 1.1rem;
  height: 1.1rem;
  color: #d64545;
  display: block;
  cursor: help;
}
.restaurant-station-select__prefix.is-locked {
  color: #d64545;
}
.restaurant-station-select__state {
  position: absolute;
  top: 50%;
  right: 1.35rem;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  background: #2eb85c;
  transform: translateY(-50%);
  box-shadow: 0 0 0 2px rgba(46, 184, 92, 0.18);
  pointer-events: none;
}
.restaurant-station-select__control.is-assigned { border-color: rgba(76, 96, 218, 0.55); background: linear-gradient(180deg, #f4f6ff 0%, #eef3ff 100%); }
.restaurant-station-select__control.is-assigned .restaurant-station-select__icon { color: var(--primary, #4c60da); }
.restaurant-station-select__control.is-assigned .restaurant-station-select__state { background: var(--primary, #4c60da); box-shadow: 0 0 0 2px rgba(76, 96, 218, 0.18); }
.restaurant-station-select__control.is-conflict { border-color: rgba(199, 158, 28, 0.8); background: linear-gradient(180deg, #fffaf1 0%, #fff6e6 100%); }
.restaurant-station-select__control.is-conflict .restaurant-station-select__icon { color: #856404; }
.restaurant-station-select__control.is-conflict .restaurant-station-select__state { background: #f0ad4e; box-shadow: 0 0 0 2px rgba(240, 173, 78, 0.18); }
.restaurant-station-select__spinner { position: absolute; top: 50%; right: 1.15rem; margin-top: -0.5rem; pointer-events: none; }
.restaurant-station-select__link { display: inline-block; margin-top: 0.3rem; font-size: 0.8rem; }
.restaurant-station-select__link:focus-visible { outline: 2px solid var(--aw-form-focus-border); outline-offset: 3px; border-radius: 2px; }
@media (max-width: 767.98px) {
  .restaurant-station-select__link { padding: 0.3rem 0; }
}
@media (pointer: coarse) {
  .restaurant-station-select__control,
  .restaurant-station-select__control select,
  .restaurant-station-select--compact .restaurant-station-select__control,
  .restaurant-station-select--compact .restaurant-station-select__control select { min-height: 44px; }
  .restaurant-station-select__hint,
  .restaurant-station-select--compact .restaurant-station-select__hint { width: 44px; height: 100%; }
  .restaurant-station-select__control select,
  .restaurant-station-select--compact .restaurant-station-select__control select { padding-left: 44px; }
}
@media (prefers-reduced-motion: reduce) {
  .restaurant-station-select__spinner { animation: none; }
}
</style>
